<script setup lang="ts">
import { computed } from 'vue';
import { $t } from '@teachino/shared-i18n';
import HugeIcon from '../../HugeIcon/HugeIcon.vue';
import TTooltip from '../../TTooltip.vue';
import type { NavigationBarProfileItemProps } from './navigation-bar-profile-item';

const props = defineProps<NavigationBarProfileItemProps>();

const PROGRESS_MIN_PERCENT = 0;
const PROGRESS_MAX_PERCENT = 100;

type ProfileProgress = {
  daysLeft: number;
  daysTotal: number;
  labelKey:
    'navigation.profile.trial_days_left' | 'navigation.profile.grace_days_left';
};

const profileProgress = computed((): ProfileProgress | null => {
  if (props.compact) {
    return null;
  }

  if (
    props.trialDaysTotal !== undefined &&
    props.trialDaysLeft !== undefined &&
    props.trialDaysTotal > 0
  ) {
    return {
      daysLeft: props.trialDaysLeft,
      daysTotal: props.trialDaysTotal,
      labelKey: 'navigation.profile.trial_days_left',
    };
  }

  if (
    props.graceDaysTotal !== undefined &&
    props.graceDaysLeft !== undefined &&
    props.graceDaysTotal > 0
  ) {
    return {
      daysLeft: props.graceDaysLeft,
      daysTotal: props.graceDaysTotal,
      labelKey: 'navigation.profile.grace_days_left',
    };
  }

  return null;
});

const progressPercent = computed(() => {
  if (!profileProgress.value) {
    return PROGRESS_MIN_PERCENT;
  }

  const rawPercent =
    (profileProgress.value.daysLeft / profileProgress.value.daysTotal) *
    PROGRESS_MAX_PERCENT;

  return Math.min(
    PROGRESS_MAX_PERCENT,
    Math.max(PROGRESS_MIN_PERCENT, Math.round(rawPercent)),
  );
});

const progressLabel = computed(() => {
  if (!profileProgress.value) {
    return '';
  }

  return $t(profileProgress.value.labelKey, {
    days: profileProgress.value.daysLeft.toString(),
  });
});
</script>

<template>
  <TTooltip
    :content="props.compact ? props.label : null"
    placement="right"
    class="flex w-full min-w-0 items-center justify-center"
  >
    <button
      class="flex items-center rounded-lg border border-slate-200 p-2 text-sm text-neutral-700 transition-all duration-200 ease-out hover:bg-white hover:shadow-sm"
      :class="[
        props.compact
          ? 'h-10 max-h-10 w-10 max-w-10 justify-center'
          : 'w-full max-w-full min-w-0 justify-start gap-4 overflow-hidden hover:translate-x-1',
      ]"
    >
      <div
        class="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-slate-200 text-slate-700"
      >
        <HugeIcon icon="user" />
      </div>

      <div
        v-if="!props.compact"
        class="flex min-w-0 flex-1 flex-col gap-1 text-left"
      >
        <span class="block min-w-0 truncate font-semibold">
          {{ props.label }}
        </span>
        <span class="block min-w-0 truncate font-normal">
          {{ props.username }}
        </span>
        <div v-if="profileProgress" class="flex flex-col gap-1">
          <div
            class="h-1.5 w-full overflow-hidden rounded-full bg-slate-200"
            role="progressbar"
            :aria-label="progressLabel"
            :aria-valuemin="PROGRESS_MIN_PERCENT"
            :aria-valuemax="PROGRESS_MAX_PERCENT"
            :aria-valuenow="progressPercent"
          >
            <div
              class="bg-primary-500 h-full rounded-full transition-[width] duration-200 ease-out"
              :style="{ width: `${progressPercent}%` }"
            />
          </div>
          <span class="block min-w-0 truncate text-xs text-slate-500">
            {{ progressLabel }}
          </span>
        </div>
      </div>
    </button>
  </TTooltip>
</template>
